import React from 'react';
import { PUBLIC_URL } from '../utils/const';
import { useTranslation } from 'react-i18next';
import { blogs } from '../_blog/routes';
import { getBlogByLanguage } from '../utils/blog';
import { Anchor, Center, Container, Grid, Group, Stack, Text } from '@mantine/core';
import { IconBrandLinkedin } from '@tabler/icons-react';
import landingGroups from '../content/landingPaths.json';
import { landingCopy, LandingId } from '../content/landingCopy';
import { contentLocale, useLanguageHref, useLocalizedPath } from '../utils/localeNav';
import { isLocale, Locale } from '../utils/seo';
import { useLocation } from 'react-router-dom';

const Footer: React.FC<{ onlyRequired?: boolean; dashboardContact?: boolean }> = ({ onlyRequired = false, dashboardContact = false }) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const href = useLocalizedPath();
  const languageHref = useLanguageHref();
  const resolved = isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : null;
  const lang: Locale = contentLocale(pathname) ?? resolved ?? 'en';

  if (onlyRequired) {
    return <>
      <Container size="lg" py="md" component="footer">
        <Stack gap="sm" align="center">
          {dashboardContact && (
            <Text size="sm" ta="center" maw={560}>
              {t('footer.dashboard_contact_prefix')}{' '}
              <Anchor href="mailto:support@calensync.freshchatemail04.com">support@calensync.freshchatemail04.com</Anchor>
            </Text>
          )}
          <Center>
            <Group gap='lg'>
              <Anchor href={href('/')}>{t('footer.home')}</Anchor>
              <Anchor href={href('/tos')}>{t("footer.tos")}</Anchor>
              <Anchor href={href('/privacy')}>{t("footer.privacy")}</Anchor>
              <Anchor href={href('/google-privacy')}>{t("footer.google_disclosure")}</Anchor>
              <Text>Calensync © {new Date().getFullYear()}</Text>
            </Group>
          </Center>
        </Stack>
      </Container>
    </>;
  }

  return (
    <Container size="lg" py="md" component="footer">
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, xxl: 4 }}>
          <Stack gap="2">
            <Anchor href={href('/tos')}>{t("footer.tos")}</Anchor>
            <Anchor href={href('/privacy')}>{t("footer.privacy")}</Anchor>
            <Anchor href={href('/google-privacy')}>{t("footer.google_disclosure")}</Anchor>
            <Anchor href={languageHref('en')}>English</Anchor>
            <Anchor href={languageHref('fr')}>Français</Anchor>
            <Anchor href={languageHref('es')}>Español</Anchor>
            <Anchor href={languageHref('it')}>Italiano</Anchor>
            <Group align="center" py='auto'>
              <Text>Calensync © {new Date().getFullYear()}</Text>
              <Anchor rel="nofollow" href="https://www.linkedin.com/company/calensync/" target="_blank"><Center><IconBrandLinkedin size={20} /></Center></Anchor>
            </Group>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, xxl: 4 }}>
          <Stack gap="2">
            <Anchor href={href('/blog')}>{t("footer.blog")}</Anchor>
            <Anchor href={href('/mcp')}>{t("footer.mcp")}</Anchor>
            <Anchor href={href('/for-freelancers')}>{t("footer.for_freelancers")}</Anchor>
            {landingGroups.map((group) => {
              const copy = landingCopy[lang as Locale][group.id as LandingId];
              const path = group.paths[lang as Locale];
              return (
                <Anchor key={group.id} href={`${PUBLIC_URL}${path}/`}>
                  {copy.nav}
                </Anchor>
              );
            })}
            <Anchor href={`${getBlogByLanguage(blogs['calensync-vs-notion'], i18n.resolvedLanguage).url}`}>
              Calensync vs Notion
            </Anchor>
            <Anchor href={`${getBlogByLanguage(blogs['calensync-vs-onecal'], i18n.resolvedLanguage).url}`}>
              Calensync vs OneCal
            </Anchor>
            <Anchor href={`${getBlogByLanguage(blogs['calensync-vs-syncthemcalendars'], i18n.resolvedLanguage).url}`}>
              Calensync vs SyncThemCalendars
            </Anchor>
          </Stack>
        </Grid.Col>

        {onlyRequired && (
          <Grid.Col span={12}>
            <Stack gap="xs">
              <Text>📧 support@calensync.live</Text>
              <Anchor rel="nofollow" href="https://tally.so/r/nroQgv" target="_blank">💬 Feature Request / Issue</Anchor>
            </Stack>
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
};

export default Footer;
